import { forwardRef, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { ParagraphDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';
import { ParagraphListEmpty } from '@/Domain/components/KnowledgeBase/ParagraphsSheet/ParagraphList/Empty/ParagraphList.empty';
import { ParagraphItemForm } from '@/Domain/components/KnowledgeBase/ParagraphsSheet/ParagraphList/ParagraphItemForm';
import { ParagraphListSkeleton } from '@/Domain/components/KnowledgeBase/ParagraphsSheet/ParagraphList/Skeleton/ParagraphList.skeleton';
import { ParagraphListItemSkeleton } from '@/Domain/components/KnowledgeBase/ParagraphsSheet/ParagraphList/Skeleton/ParagraphListItem.skeleton';
import { TooltipProvider } from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ArticleTypeEnum } from '@/Domain/enum/ArticleType.enum';
import { useCreateParagraph } from '@/Domain/hooks/KnowledgeBase/useCreateParagraph/useCreateParagraph.hook';
import { useFetchParagraphs } from '@/Domain/hooks/KnowledgeBase/useFetchParagraphs.hook';

type Props = {
  query: string;
};

export const ParagraphList = forwardRef<HTMLDivElement | null, Props>(({ query }, ref) => {
  const { t } = useTranslation('paragraphs');
  const { data, isLoading, sentryRef, hasNextPage } = useFetchParagraphs(query);
  const { handleCreateParagraph } = useCreateParagraph();

  const [newlyCreatedParagraphId, setNewlyCreatedParagraphId] = useState<string | number | null>(
    null,
  );

  const paragraphs = data?.pages.flatMap((page) => page.items) ?? [];

  useHotkeys(
    'enter',
    async (event) => {
      event.preventDefault();
      try {
        const newParagraph = await handleCreateParagraph({
          type: ArticleTypeEnum.Paragraph,
          content: '',
        });
        setNewlyCreatedParagraphId(newParagraph.id);
      } catch (error) {
        console.error('Error creating paragraph:', error);
      }
    },
    [],
    {
      enableOnFormTags: ['input'],
      preventDefault: true,
    },
  );

  useEffect(() => {
    if (newlyCreatedParagraphId) {
      const timer = setTimeout(() => {
        setNewlyCreatedParagraphId(null);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [newlyCreatedParagraphId]);

  if (isLoading) return <ParagraphListSkeleton />;

  if (!paragraphs.length) return <ParagraphListEmpty />;

  return (
    <TooltipProvider>
      <div ref={ref} className="flex flex-col custom-scroll overflow-y-auto flex-1 h-full">
        <section className="grid grid-cols-[0.15fr_1fr_0.01fr] gap-x-4 bg-accent px-4 py-3 text-sm font-medium text-muted-foreground">
          <span>{t('modal.no')}</span>
          <span className="pl-4">{t('modal.snippet')}</span>
          <span />
        </section>

        {paragraphs.map((paragraph: ParagraphDto, index: number) => (
          <ParagraphItemForm
            key={paragraph.id}
            paragraph={paragraph}
            index={index}
            shouldFocus={paragraph.id === newlyCreatedParagraphId}
          />
        ))}

        {hasNextPage && (
          <div ref={sentryRef}>
            {Array.from({ length: 3 }).map((_, index) => (
              <ParagraphListItemSkeleton key={index} index={index} withIndex={false} />
            ))}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
});
